/**
 * IndexedDB persistence for chat histories + in-flight stream snapshots.
 *
 * Phase 4 of the PWA streaming fix. Cold restart should restore exactly what
 * was on screen so the UI paints last-known state instantly, then catches up
 * via WS replay using the persisted lastSeq per chat.
 *
 * Two IDB stores, both via idb-keyval:
 *   - jarvis-chat-histories: key=chatId, value={ messages, updatedAt, bytes }
 *   - jarvis-chat-streams:   key=chatId, value={ content, lastSeq, updatedAt, bytes }
 *
 * LRU eviction caps combined usage at IDB_CAP_BYTES (25 MB). Small accounting
 * records keep writes and lazy reads from fetching every transcript body.
 * Streams are never evicted while active; only histories are evicted.
 *
 * Debounced per-chat snapshotting (250 ms): the router fires routeChunk for
 * every incoming chunk, and per-chat debounce buckets collapse a stream of
 * seq bumps into a single IDB write per quiet window.
 */

import {
  get,
  getMany,
  setMany,
  delMany,
  update,
  keys,
  createStore,
  type UseStore,
} from 'idb-keyval';
import type { ChatMessage } from '../../core/api/types';

export const IDB_CAP_BYTES = 25 * 1024 * 1024;
export const SNAPSHOT_DEBOUNCE_MS = 250;

const historyStore = createStore('jarvis-chat-histories', 'kv');
const streamStore = createStore('jarvis-chat-streams', 'kv');
const META_PREFIX = '__jarvis_accounting__:';

// In-memory byte accumulators are refreshed from small metadata entries before
// cap enforcement. Legacy records without metadata are migrated once.
let _totalBytes = 0;
const _historyBytes = new Map<number, number>();
const _streamBytes = new Map<number, number>();
const _historyUpdatedAt = new Map<number, number>();
let _accountingRefresh: Promise<void> | null = null;
let _mutationQueue = Promise.resolve();

interface HistoryRecord {
  messages: ChatMessage[];
  updatedAt: number;
  bytes: number;
}

interface StreamRecord {
  content: string;
  lastSeq: number;
  updatedAt: number;
  bytes: number;
}

interface AccountingRecord {
  bytes: number;
  updatedAt: number;
}

export interface LoadedSnapshot {
  chatId: number;
  content: string;
  lastSeq: number;
  updatedAt: number;
}

function isChatMessage(value: unknown): value is ChatMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<ChatMessage>;
  return (message.role === 'user' || message.role === 'assistant')
    && typeof message.content === 'string'
    && typeof message.timestamp === 'string'
    && (message.attachments === undefined || Array.isArray(message.attachments));
}

function approxBytes(value: unknown): number {
  try {
    return JSON.stringify(value).length;
  } catch {
    return 0;
  }
}

function nowMs(): number {
  return Date.now();
}

function metadataKey(chatId: number): string {
  return `${META_PREFIX}${chatId}`;
}

function isAccountingRecord(value: unknown): value is AccountingRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<AccountingRecord>;
  return Number.isFinite(record.bytes) && record.bytes! >= 0
    && Number.isFinite(record.updatedAt) && record.updatedAt! >= 0;
}

function enqueueMutation<T>(operation: () => Promise<T>): Promise<T> {
  const next = _mutationQueue.catch(() => {}).then(operation);
  _mutationQueue = next.then(() => undefined, () => undefined);
  return next;
}

// ------------------------------------------------------------------
// Debounced per-chat snapshot
// ------------------------------------------------------------------

interface PendingSnapshot {
  timer: ReturnType<typeof setTimeout>;
  content: string;
  lastSeq: number;
}

const _snapshotTimers = new Map<number, PendingSnapshot>();
const _streamOperations = new Map<number, Promise<void>>();

function recalculateTotalBytes(): void {
  _totalBytes = 0;
  for (const bytes of _historyBytes.values()) _totalBytes += bytes;
  for (const bytes of _streamBytes.values()) _totalBytes += bytes;
}

async function readAccounting(
  store: UseStore,
): Promise<{ records: Map<number, AccountingRecord>; migrated: [IDBValidKey, AccountingRecord][] }> {
  const allKeys = await keys(store);
  const chatIds = allKeys.filter((key): key is number => typeof key === 'number');
  const metadata = await getMany<unknown>(chatIds.map(metadataKey), store);
  const records = new Map<number, AccountingRecord>();
  const missing: number[] = [];

  chatIds.forEach((chatId, index) => {
    const value = metadata[index];
    if (isAccountingRecord(value)) records.set(chatId, value);
    else missing.push(chatId);
  });

  // Records created before the metadata index are migrated in the background.
  // Only this one-time compatibility path reads transcript bodies.
  const legacy = await getMany<HistoryRecord | StreamRecord | undefined>(missing, store);
  const migrated: [IDBValidKey, AccountingRecord][] = [];
  legacy.forEach((record, index) => {
    if (!record || typeof record !== 'object') return;
    const chatId = missing[index];
    const accounting = {
      bytes: Number.isFinite(record.bytes) && record.bytes >= 0
        ? record.bytes
        : approxBytes(record),
      updatedAt: Number.isFinite(record.updatedAt) && record.updatedAt >= 0
        ? record.updatedAt
        : 0,
    };
    records.set(chatId, accounting);
    migrated.push([metadataKey(chatId), accounting]);
  });

  return { records, migrated };
}

async function persistMissingAccounting(
  entries: [IDBValidKey, AccountingRecord][],
  store: UseStore,
): Promise<void> {
  await Promise.all(entries.map(([key, metadata]) => update(
    key,
    (current: unknown) => isAccountingRecord(current) ? current : metadata,
    store,
  )));
}

async function refreshAccounting(): Promise<void> {
  if (_accountingRefresh) return _accountingRefresh;
  _accountingRefresh = (async () => {
    const [histories, streams] = await Promise.all([
      readAccounting(historyStore),
      readAccounting(streamStore),
    ]);

    _historyBytes.clear();
    _historyUpdatedAt.clear();
    for (const [chatId, metadata] of histories.records) {
      _historyBytes.set(chatId, metadata.bytes);
      _historyUpdatedAt.set(chatId, metadata.updatedAt);
    }
    _streamBytes.clear();
    for (const [chatId, metadata] of streams.records) {
      _streamBytes.set(chatId, metadata.bytes);
    }
    recalculateTotalBytes();

    await Promise.all([
      persistMissingAccounting(histories.migrated, historyStore),
      persistMissingAccounting(streams.migrated, streamStore),
    ]);
  })().finally(() => {
    _accountingRefresh = null;
  });
  return _accountingRefresh;
}

function enqueueStreamOperation(chatId: number, operation: () => Promise<void>): Promise<void> {
  const previous = _streamOperations.get(chatId) ?? Promise.resolve();
  const next = previous.catch(() => {}).then(operation);
  _streamOperations.set(chatId, next);
  const cleanup = () => {
    if (_streamOperations.get(chatId) === next) _streamOperations.delete(chatId);
  };
  void next.then(cleanup, cleanup);
  return next;
}

/**
 * Snapshot an in-flight stream's content + lastSeq. Debounces per chat so a
 * burst of chunks produces a single IDB write. Call on every routeChunk that
 * advances lastSeq.
 *
 * Returns synchronously; the actual write happens after the debounce window.
 */
export function snapshotStream(
  chatId: number,
  content: string,
  lastSeq: number,
): void {
  const existing = _snapshotTimers.get(chatId);
  if (existing) clearTimeout(existing.timer);

  const timer = setTimeout(() => {
    const pending = _snapshotTimers.get(chatId);
    _snapshotTimers.delete(chatId);
    if (!pending) return;
    enqueueStreamOperation(
      chatId,
      () => writeStreamSnapshot(chatId, pending.content, pending.lastSeq),
    ).catch(
      (err) => {
        console.warn('[chat-persist] snapshotStream write failed:', err);
      },
    );
  }, SNAPSHOT_DEBOUNCE_MS);

  _snapshotTimers.set(chatId, { timer, content, lastSeq });
}

/**
 * Drop pending snapshot debounces without writing them. Call when a stream
 * is being replaced (new beginStream on the same chat) or torn down — we
 * don't want a stale debounce to overwrite fresh state with old content.
 */
export function cancelPendingSnapshot(chatId: number): void {
  const pending = _snapshotTimers.get(chatId);
  if (!pending) return;
  clearTimeout(pending.timer);
  _snapshotTimers.delete(chatId);
}

/**
 * Test helper: force all pending debounced snapshots to write synchronously.
 * Returns once every pending write has been awaited.
 */
export async function __flushSnapshotsForTests(): Promise<void> {
  const entries = Array.from(_snapshotTimers.entries());
  _snapshotTimers.clear();
  for (const [chatId, pending] of entries) {
    clearTimeout(pending.timer);
    await enqueueStreamOperation(
      chatId,
      () => writeStreamSnapshot(chatId, pending.content, pending.lastSeq),
    );
  }
}

async function writeStreamSnapshot(
  chatId: number,
  content: string,
  lastSeq: number,
): Promise<void> {
  await enqueueMutation(async () => {
    await refreshAccounting();
    const rec: StreamRecord = {
      content,
      lastSeq,
      updatedAt: nowMs(),
      bytes: 0,
    };
    rec.bytes = approxBytes(rec);

    await setMany([
      [chatId, rec],
      [metadataKey(chatId), { bytes: rec.bytes, updatedAt: rec.updatedAt }],
    ], streamStore);
    // Re-read the lightweight index after the write so another tab's
    // concurrent write is included before cap enforcement.
    await refreshAccounting();
    await enforceCap();
  });
}

/**
 * Remove an in-flight stream entry. Call when the stream finalizes (done/error).
 * The assistant message is saved to history via `persistHistory` separately.
 */
export async function clearStream(chatId: number): Promise<void> {
  cancelPendingSnapshot(chatId);
  await enqueueStreamOperation(chatId, async () => {
    await enqueueMutation(async () => {
      await refreshAccounting();
      await delMany([chatId, metadataKey(chatId)], streamStore);
      await refreshAccounting();
    });
  });
}

// ------------------------------------------------------------------
// Histories
// ------------------------------------------------------------------

/**
 * Persist the full message history for a chat. Call on client-initiated
 * mutations (user send, assistant finalize). Do NOT call on server fetches
 * that merely mirror authoritative state — that risks silently persisting a
 * truncated server response over a richer local record.
 */
export async function persistHistory(
  chatId: number,
  messages: ChatMessage[],
): Promise<void> {
  await enqueueMutation(async () => {
    await refreshAccounting();
    const rec: HistoryRecord = {
      messages,
      updatedAt: nowMs(),
      bytes: 0,
    };
    rec.bytes = approxBytes(rec);

    await setMany([
      [chatId, rec],
      [metadataKey(chatId), { bytes: rec.bytes, updatedAt: rec.updatedAt }],
    ], historyStore);
    await refreshAccounting();
    await enforceCap();
  });
}

/**
 * Load persisted chat histories. Passing chatIds fetches only those transcript
 * bodies and records their lightweight metadata. Full accounting, including
 * one-time legacy migration, waits until a mutation needs cap enforcement.
 * Omitting chatIds retains the compatibility behavior of loading every history.
 */
export async function loadHistories(
  chatIds?: number[],
): Promise<Record<number, ChatMessage[]>> {
  const out: Record<number, ChatMessage[]> = {};
  const selectedIds = chatIds === undefined
    ? (await keys(historyStore)).filter((key): key is number => typeof key === 'number')
    : Array.from(new Set(chatIds.filter(Number.isFinite)));
  const records = await getMany<HistoryRecord | undefined>(selectedIds, historyStore);

  if (chatIds === undefined) {
    _historyBytes.clear();
    _historyUpdatedAt.clear();
  }

  const metadataUpdates: [IDBValidKey, AccountingRecord][] = [];
  selectedIds.forEach((chatId, index) => {
    const rec = records[index];
    if (!rec || !Array.isArray(rec.messages)) return;
    const bytes = Number.isFinite(rec.bytes) && rec.bytes >= 0
      ? rec.bytes
      : approxBytes(rec);
    const updatedAt = Number.isFinite(rec.updatedAt) && rec.updatedAt >= 0
      ? rec.updatedAt
      : 0;
    out[chatId] = rec.messages.filter(isChatMessage);
    _historyBytes.set(chatId, bytes);
    _historyUpdatedAt.set(chatId, updatedAt);
    metadataUpdates.push([metadataKey(chatId), { bytes, updatedAt }]);
  });
  recalculateTotalBytes();
  if (metadataUpdates.length > 0) {
    await persistMissingAccounting(metadataUpdates, historyStore);
  }
  return out;
}

/** Delete a history entry (e.g., when a chat is deleted server-side). */
export async function clearHistory(chatId: number): Promise<void> {
  await enqueueMutation(async () => {
    await refreshAccounting();
    await delMany([chatId, metadataKey(chatId)], historyStore);
    await refreshAccounting();
  });
}

// ------------------------------------------------------------------
// Stream snapshots (load path)
// ------------------------------------------------------------------

/**
 * Load all in-flight stream snapshots. Used at app boot, before WS connects,
 * to seed chatStreamStates so the UI shows last-known streaming content
 * instantly. WS replay then fills in anything new via seq > lastSeq.
 */
export async function loadStreamSnapshots(): Promise<LoadedSnapshot[]> {
  const out: LoadedSnapshot[] = [];
  _streamBytes.clear();
  const chatIds = (await keys(streamStore))
    .filter((key): key is number => typeof key === 'number');
  const records = await getMany<StreamRecord | undefined>(chatIds, streamStore);
  const metadataUpdates: [IDBValidKey, AccountingRecord][] = [];
  chatIds.forEach((chatId, index) => {
    const rec = records[index];
    if (
      !rec
      || typeof rec.content !== 'string'
      || !Number.isFinite(rec.lastSeq)
      || rec.lastSeq < 0
    ) return;
    const updatedAt = Number.isFinite(rec.updatedAt) && rec.updatedAt > 0
      ? rec.updatedAt
      : 0;
    out.push({
      chatId,
      content: rec.content,
      lastSeq: rec.lastSeq,
      updatedAt,
    });
    const bytes = Number.isFinite(rec.bytes) && rec.bytes >= 0
      ? rec.bytes
      : approxBytes(rec);
    _streamBytes.set(chatId, bytes);
    metadataUpdates.push([metadataKey(chatId), { bytes, updatedAt }]);
  });
  recalculateTotalBytes();
  if (metadataUpdates.length > 0) {
    await persistMissingAccounting(metadataUpdates, streamStore);
  }
  return out;
}

// ------------------------------------------------------------------
// LRU cap enforcement
// ------------------------------------------------------------------

/**
 * Enforce the 25 MB cap. Fast path: consult the in-memory accumulator and
 * return immediately if we're under. Slow path: only when eviction is
 * actually needed do we read history records from IDB to get their
 * timestamps for LRU ordering. Streams are never evicted — they represent
 * live in-flight work.
 *
 * Ties on `updatedAt` are broken by `chatId` ascending so eviction is
 * deterministic across two histories written in the same millisecond.
 */
async function enforceCap(): Promise<void> {
  if (_totalBytes <= IDB_CAP_BYTES) return;

  const candidates = Array.from(_historyBytes.keys(), (chatId) => ({
    chatId,
    updatedAt: _historyUpdatedAt.get(chatId) ?? 0,
  }));

  candidates.sort((a, b) => {
    if (a.updatedAt !== b.updatedAt) {
      return a.updatedAt - b.updatedAt;
    }
    return a.chatId - b.chatId;
  });

  for (const { chatId } of candidates) {
    if (_totalBytes <= IDB_CAP_BYTES) break;
    const bytes = _historyBytes.get(chatId) ?? 0;
    await delMany([chatId, metadataKey(chatId)], historyStore);
    _historyBytes.delete(chatId);
    _historyUpdatedAt.delete(chatId);
    recalculateTotalBytes();
    console.warn(
      `[chat-persist] LRU-evicted history for chat ${chatId} (${bytes} B)`,
    );
  }
}

// ------------------------------------------------------------------
// Test helpers
// ------------------------------------------------------------------

/** Test-only: clear both stores + in-memory accumulators. */
export async function __resetAllPersistedForTests(): Promise<void> {
  for (const [, pending] of _snapshotTimers) clearTimeout(pending.timer);
  _snapshotTimers.clear();
  await Promise.allSettled(_streamOperations.values());
  _streamOperations.clear();
  await _mutationQueue;
  await _accountingRefresh?.catch(() => {});
  const hist = await keys(historyStore);
  const streams = await keys(streamStore);
  await Promise.all([
    hist.length > 0 ? delMany(hist, historyStore) : Promise.resolve(),
    streams.length > 0 ? delMany(streams, streamStore) : Promise.resolve(),
  ]);
  _historyBytes.clear();
  _historyUpdatedAt.clear();
  _streamBytes.clear();
  _totalBytes = 0;
}

/** Test-only: current accumulator total (matches on-disk byte totals). */
export async function __totalBytesForTests(): Promise<number> {
  await _mutationQueue;
  await _accountingRefresh?.catch(() => {});
  return _totalBytes;
}

/** Test-only: verify accumulator matches disk. Used in audit regression test. */
export async function __verifyAccumulatorForTests(): Promise<boolean> {
  await _mutationQueue;
  await _accountingRefresh?.catch(() => {});
  let total = 0;
  for (const k of await keys(historyStore)) {
    if (typeof k !== 'number') continue;
    const rec = (await get(k, historyStore)) as HistoryRecord | undefined;
    if (rec) total += Number.isFinite(rec.bytes) ? rec.bytes : approxBytes(rec);
  }
  for (const k of await keys(streamStore)) {
    if (typeof k !== 'number') continue;
    const rec = (await get(k, streamStore)) as StreamRecord | undefined;
    if (rec) total += Number.isFinite(rec.bytes) ? rec.bytes : approxBytes(rec);
  }
  return total === _totalBytes;
}
